import type { WebGPURenderer } from 'three/webgpu';
import type { GpuStats, PerfGpuTimer } from './GpuTimer';

const SAMPLE_LIMIT = 120;

/**
 * GPU timing for the WebGPU backend. Three.js exposes per-frame GPU
 * duration via `renderer.info.render.timestamp` (and `compute.timestamp`)
 * once the renderer is constructed with `trackTimestamp: true` (see
 * `Renderer.ts:209`) and `resolveTimestampsAsync(type)` has been awaited.
 * We fire the resolves fire-and-forget every tick; samples arrive ~1-3
 * frames late, identical to the WebGL EXT_disjoint_timer_query path.
 *
 * Render and compute live in separate timestamp pools, surfaced as
 * `gpu.frame` (render) and `gpu.compute` (TSL `.compute()` dispatches).
 * Three r184 injects timestamp writes at render/compute pass boundaries but
 * does not expose arbitrary TSL subpass timestamps, so labels passed through
 * `measure()` are associated with the whole measured render group. This keeps
 * PerfHud/PerfLab rows honest: WebGPU can show `gpu.scene`,
 * `gpu.bloom`, or `gpu.postfx.webgpuRenderPipeline`, but not fake
 * bloom-threshold/blur internals until Three exposes public hooks.
 */
export class WebGPUGpuTimer implements PerfGpuTimer {
  private readonly renderer: WebGPURenderer;
  private readonly renderSamples: number[] = [];
  private readonly computeSamples: number[] = [];
  private readonly labeledSamples = new Map<string, number[]>();
  private readonly pendingRenderLabelFrames: string[][] = [];
  private readonly pendingComputeLabelFrames: string[][] = [];
  private activeRenderLabels = new Set<string>();
  private activeComputeLabels = new Set<string>();
  private resolvingRender = false;
  private resolvingCompute = false;
  private hasFirstRenderSample = false;

  constructor(renderer: WebGPURenderer) {
    this.renderer = renderer;
  }

  available(): boolean {
    return this.hasFirstRenderSample;
  }

  begin(name: string): void {
    this.activeRenderLabels.add(name);
  }

  end(): void { /* labels are drained once per frame in tick() */ }

  measure(name: string, fn: () => void): void {
    this.begin(name);
    try {
      fn();
    } finally {
      this.end();
    }
  }

  measureCompute(name: string, fn: () => void): void {
    this.activeComputeLabels.add(name);
    try {
      fn();
    } catch (err) {
      this.activeComputeLabels.delete(name);
      throw err;
    }
  }

  tick(): void {
    if (this.activeRenderLabels.size > 0) {
      this.pendingRenderLabelFrames.push([...this.activeRenderLabels]);
      this.activeRenderLabels.clear();
    }
    this.drain('render');
  }

  tickCompute(): void {
    if (this.activeComputeLabels.size > 0) {
      this.pendingComputeLabelFrames.push([...this.activeComputeLabels]);
      this.activeComputeLabels.clear();
    }
    this.drain('compute');
  }

  /** Shared resolver for both render + compute pools. */
  private drain(type: 'render' | 'compute'): void {
    // resolveTimestampsAsync maps the GPU's BigUint64Array result; if a
    // resolve is in flight, skip rather than queueing — the next tick
    // will pick up the latest. Without this guard a slow GPU would
    // accumulate pending mapAsync calls and starve the JS thread.
    const inFlight = type === 'render' ? this.resolvingRender : this.resolvingCompute;
    if (inFlight) return;
    if (type === 'render') this.resolvingRender = true;
    else this.resolvingCompute = true;

    const renderer = this.renderer as WebGPURenderer & {
      resolveTimestampsAsync(type?: 'render' | 'compute'): Promise<number | undefined>;
    };
    void renderer.resolveTimestampsAsync(type).then((duration) => {
      if (type === 'render') this.resolvingRender = false;
      else this.resolvingCompute = false;

      const fromInfo = (this.renderer.info as {
        render: { timestamp: number };
        compute: { timestamp: number };
      })[type].timestamp;
      const ms = typeof duration === 'number' && Number.isFinite(duration) ? duration : fromInfo;
      const hasPendingLabels = (type === 'render' ? this.pendingRenderLabelFrames : this.pendingComputeLabelFrames).length > 0;
      if (typeof ms === 'number' && Number.isFinite(ms) && (ms > 0 || (type === 'compute' && hasPendingLabels))) {
        const samples = type === 'render' ? this.renderSamples : this.computeSamples;
        samples.push(ms);
        if (samples.length > SAMPLE_LIMIT) samples.shift();
        if (type === 'render') this.hasFirstRenderSample = true;
        this.recordLabels(type, ms);
      }
    }).catch(() => {
      if (type === 'render') this.resolvingRender = false;
      else this.resolvingCompute = false;
    });
  }

  stats(): Record<string, GpuStats> {
    const out: Record<string, GpuStats> = {};
    if (this.renderSamples.length > 0) out['gpu.frame'] = summarize(this.renderSamples);
    if (this.computeSamples.length > 0) out['gpu.compute'] = summarize(this.computeSamples);
    for (const [name, samples] of this.labeledSamples) {
      if (samples.length > 0) out[name] = summarize(samples);
    }
    return out;
  }

  /** Render mean only — drives the HUD's at-a-glance "GPU XX.Xms" line. */
  totalMean(): number {
    if (this.renderSamples.length === 0) return 0;
    return this.renderSamples.reduce((a, b) => a + b, 0) / this.renderSamples.length;
  }

  reset(): void {
    this.renderSamples.length = 0;
    this.computeSamples.length = 0;
    this.labeledSamples.clear();
    this.pendingRenderLabelFrames.length = 0;
    this.pendingComputeLabelFrames.length = 0;
    this.activeRenderLabels.clear();
    this.activeComputeLabels.clear();
    this.hasFirstRenderSample = false;
  }

  private recordLabels(type: 'render' | 'compute', ms: number): void {
    const queue = type === 'render' ? this.pendingRenderLabelFrames : this.pendingComputeLabelFrames;
    const labels = queue.shift();
    if (!labels || labels.length === 0) return;
    for (const label of labels) {
      let samples = this.labeledSamples.get(label);
      if (!samples) {
        samples = [];
        this.labeledSamples.set(label, samples);
      }
      samples.push(ms);
      if (samples.length > SAMPLE_LIMIT) samples.shift();
    }
  }
}

function summarize(samples: number[]): GpuStats {
  const sorted = [...samples].sort((a, b) => a - b);
  const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
  const p95Idx = Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95));
  return { mean, p95: sorted[p95Idx], max: sorted[sorted.length - 1], count: samples.length };
}
